import ReactMarkdown from "react-markdown";

import { cn } from "@/lib/utils";

export function WikiDocumentMarkdown({
  text,
  compact = false,
  className,
}: {
  text: string;
  compact?: boolean;
  className?: string;
}): JSX.Element {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return <p className="text-sm text-slate-500">This Outline page has no Markdown body.</p>;
  }

  return (
    <div className={cn(compact ? "text-sm" : "", className)}>
      <ReactMarkdown
        components={{
          a: ({ children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer" className="text-brand-700 underline hover:text-brand-800">
              {children}
            </a>
          ),
          h1: ({ children }) => (
            <h1
              className={cn(
                "font-semibold text-slate-950 first:mt-0",
                compact ? "mb-3 mt-4 text-lg" : "mb-4 mt-6 text-2xl",
              )}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={cn("font-semibold text-slate-950", compact ? "mb-2 mt-4 text-base" : "mb-3 mt-6 text-xl")}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={cn("font-semibold text-slate-950", compact ? "mb-1.5 mt-3 text-sm" : "mb-2 mt-5 text-lg")}>
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className={cn("text-slate-700", compact ? "my-2 leading-6" : "my-3 leading-7")}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul className={cn("list-disc text-slate-700", compact ? "my-2 space-y-0.5 pl-5" : "my-3 space-y-1 pl-6")}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className={cn("list-decimal text-slate-700", compact ? "my-2 space-y-0.5 pl-5" : "my-3 space-y-1 pl-6")}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => <li className={compact ? "leading-6" : "leading-7"}>{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-3 border-l-4 border-slate-200 pl-3 text-slate-600">{children}</blockquote>
          ),
          code: ({ children, className: codeClassName }) => (
            <code
              className={
                codeClassName ?? "rounded bg-slate-100 px-1 py-0.5 font-mono text-sm text-slate-900"
              }
            >
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="my-3 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-6 text-slate-700 [&_code]:block [&_code]:rounded-none [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-slate-700">
              {children}
            </pre>
          ),
        }}
      >
        {trimmed}
      </ReactMarkdown>
    </div>
  );
}
